'use client';

import Box from '@mui/material/Box';
import { animated, useSpring, useSprings } from 'react-spring';
import { GRUND, djup, hexTillRgba, ljus, type Kombonamn } from 'src/components/rörelse';
import { useRörelse } from 'src/components/rörelse';
import type { Motiv } from '../presentationer';

const AnimeradDiv = animated('div');

/**
 * Sidans motiv i miniatyr.
 *
 * Indexkorten bär det här i stället för en ikon. Motivet är det som faktiskt skiljer
 * sidorna åt, så det är också det som ska stå på kortet — en ikonrad hade sagt mindre
 * och sett ut som varje annan startsida.
 */
export function Motivminiatyr({ motiv, kombo }: { motiv: Motiv; kombo: Kombonamn }) {
    const { mindreRörelse } = useRörelse();
    const antal = motiv === 'ring' ? 12 : motiv === 'blomning' ? 9 : 5;

    const [fjädrar] = useSprings(
        antal,
        (i) => ({
            from: { t: 0 },
            to: { t: 1 },
            delay: mindreRörelse ? 0 : 120 + i * 90,
            config: { tension: 180, friction: 24 },
            immediate: mindreRörelse
        }),
        [motiv, mindreRörelse]
    );

    const svep = useSpring({
        from: { p: 0 },
        to: { p: 1 },
        loop: !mindreRörelse,
        config: { duration: 3200 },
        immediate: mindreRörelse
    });

    return (
        <Box
            aria-hidden
            sx={{
                position: 'relative',
                height: 128,
                borderRadius: 1.5,
                overflow: 'hidden',
                bgcolor: hexTillRgba(djup(kombo), 0.2),
                border: `1px solid ${hexTillRgba(ljus(kombo), 0.22)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {motiv === 'ring' &&
                fjädrar.map((s, i) => {
                    const vinkel = (i / antal) * 360;
                    return (
                        <AnimeradDiv
                            key={i}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                background: ljus(kombo),
                                opacity: s.t.to((t) => 0.25 + t * 0.75),
                                transform: s.t.to(
                                    (t) =>
                                        `translate(-50%,-50%) rotate(${vinkel}deg) translateY(${-44 * t}px)`
                                )
                            }}
                        />
                    );
                })}

            {motiv === 'banor' && (
                <Box sx={{ width: '78%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {fjädrar.map((s, i) => (
                        <AnimeradDiv
                            key={i}
                            style={{
                                height: 6,
                                borderRadius: 999,
                                background: ljus(kombo),
                                transformOrigin: 'left',
                                opacity: s.t.to((t) => 0.3 + t * 0.6),
                                transform: s.t.to((t) => `scaleX(${0.24 + t * (0.4 + i * 0.14)})`)
                            }}
                        />
                    ))}
                </Box>
            )}

            {motiv === 'ström' && (
                <Box sx={{ width: '76%', display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {fjädrar.map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <AnimeradDiv
                                style={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 999,
                                    background: hexTillRgba(ljus(kombo), 0.5),
                                    opacity: s.t.to((t) => 0.3 + t * 0.7),
                                    transform: s.t.to((t) => `translateX(${(1 - t) * -18}px)`)
                                }}
                            />
                            <AnimeradDiv
                                style={{
                                    width: 16,
                                    height: 1,
                                    background: ljus(kombo),
                                    transformOrigin: 'left',
                                    transform: s.t.to((t) => `scaleX(${t})`)
                                }}
                            />
                            <AnimeradDiv
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 2,
                                    background: ljus(kombo),
                                    opacity: s.t
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {motiv === 'kedja' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    {fjädrar.map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <AnimeradDiv
                                style={{
                                    width: 13,
                                    height: 13,
                                    borderRadius: '50%',
                                    border: `1px solid ${ljus(kombo)}`,
                                    background: s.t.to((t) => (t > 0.6 ? ljus(kombo) : 'transparent')),
                                    opacity: s.t.to((t) => 0.35 + t * 0.65),
                                    transform: s.t.to((t) => `scale(${0.7 + t * 0.3})`)
                                }}
                            />
                            {i < antal - 1 ? (
                                <AnimeradDiv
                                    style={{
                                        width: 14,
                                        height: 1,
                                        background: ljus(kombo),
                                        transformOrigin: 'left',
                                        transform: s.t.to((t) => `scaleX(${t})`)
                                    }}
                                />
                            ) : null}
                        </Box>
                    ))}
                </Box>
            )}

            {motiv === 'blomning' &&
                fjädrar.map((s, i) => {
                    const vinkel = (i / antal) * 360;
                    return (
                        <AnimeradDiv
                            key={i}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                border: `1px solid ${ljus(kombo)}`,
                                background: hexTillRgba(ljus(kombo), 0.12),
                                opacity: s.t.to((t) => 0.25 + t * 0.7),
                                transform: s.t.to(
                                    (t) =>
                                        `translate(-50%,-50%) rotate(${vinkel}deg) translateY(${-38 * t}px) rotate(${-vinkel}deg)`
                                )
                            }}
                        />
                    );
                })}

            <AnimeradDiv
                style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background: `linear-gradient(90deg, transparent 0%, ${hexTillRgba(
                        ljus(kombo),
                        0.14
                    )} 50%, transparent 100%)`,
                    transform: svep.p.to((p) => `translateX(${-100 + p * 200}%)`)
                }}
            />
        </Box>
    );
}

export const MOTIVBAKGRUND = GRUND.upphöjd;
